import tokens from '../../../design/tokens/c-atlas.tokens.json'
import type { Brand } from './context'

/** The apex's own tab icon — `web/index.html` links it, and the apex keeps it. */
export const APEX_FAVICON = '/design/augrank-mark.svg'

/**
 * The brand colour and what sits on it, from the token source rather than the
 * stylesheet.
 *
 * A favicon cannot read the page's custom properties: the SVG is a data URI in
 * a `<link>`, not an element in the document, so `var(--ar-primary)` inside it
 * resolves to nothing. The value has to travel in the markup — and the one
 * place to take it from is `design/tokens`, which is where `--ar-primary` is
 * built from (`build_tokens.py`). The light palette on purpose: a tab icon does
 * not follow the theme, and the pair is the design's own contrast pairing for
 * text on the brand colour.
 */
const GROUND = tokens.color.light.primary
const INK = tokens.color.light.on_primary

/**
 * A letter mark, as an SVG data URI: the first letter of the organisation's
 * name on the brand colour, for a tenant host with no logo uploaded.
 *
 * `encodeURIComponent` on the whole document rather than a hand-escaped subset:
 * the letter is user data — an organisation named `<Ampersand>` or `"Quotes"`
 * is unusual, not impossible — and a partial escape is the shape of markup
 * injection into the browser's own chrome.
 */
export function letterMark(letter: string): string {
  const glyph = (letter.trim().charAt(0) || '?').toUpperCase()
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="14" fill="${GROUND}"/>` +
    `<text x="32" y="44" text-anchor="middle" font-family="Poppins, system-ui, sans-serif" ` +
    `font-size="36" font-weight="600" fill="${INK}">${escapeXml(glyph)}</text>` +
    `</svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

/**
 * Point the tab icon at `href`.
 *
 * Edits the `<link rel="icon">` `index.html` already carries rather than
 * appending a second one: two icon links leave the choice to the browser, and
 * browsers do not agree. Creates it only when the page has none, which is the
 * test harness's document rather than the shipped one.
 */
export function applyFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  if (link.href !== href) link.href = href
  // A data URI names its own type; the apex mark is an SVG file, and the type
  // is what lets a browser that cannot render SVG icons skip it cleanly.
  if (href.startsWith('data:')) link.removeAttribute('type')
  else if (href.endsWith('.svg')) link.type = 'image/svg+xml'
  else link.removeAttribute('type')
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * The tab icon for a brand: the tenant's logo when it has one, a letter mark
 * from its name when it has not, and AugRank's own mark everywhere else.
 */
export function faviconFor(brand: Brand): string {
  if (brand.host !== 'tenant') return APEX_FAVICON
  if (brand.logoUrl) return brand.logoUrl
  return letterMark(brand.name ?? brand.productName)
}
